import React, { HTMLAttributes } from 'react';
import { useNotificationEvent } from '@/dashboard/hooks/settings/notifications/useNotificationEvent';
import { NotificationModelProps } from '@/dashboard/models/settings/NotificationModel';
import { ChevronRightIcon } from '@heroicons/react/outline';
import { CheckCircleIcon, ExclamationCircleIcon, XCircleIcon } from '@heroicons/react/solid';
import { transformDateStrToTimeString } from '@jove-frontend/uikit';
import { Box, Button, Typography } from '@mui/material';
import clsx from 'clsx';

type NotificationsCardProps = HTMLAttributes<HTMLDivElement> & {
  model: NotificationModelProps;
};

const NotificationsCard = (props: NotificationsCardProps) => {
  const {
    className,
    onClick,
    model: {
      read,
      created_at,
      context: { detail },
      canRenew,
      canExtend,
      canJumpUrl,
      level,
    },
  } = props;

  const { jumpRenewPolicy, jumpExtendPolicy, jumpUrl } = useNotificationEvent(props.model);

  return (
    <Box className={clsx('flex cursor-pointer flex-row', className)} onClick={onClick}>
      {level === 'error' && (
        <Box className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#FCE9E9]">
          <XCircleIcon className="text-status-error h-6 w-6" />
        </Box>
      )}
      {level === 'info' && (
        <Box className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#D5F3E2]">
          <CheckCircleIcon className="text-status-success h-6 w-6" />
        </Box>
      )}
      {level === 'warning' && (
        <Box className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#FFE8DA]">
          <ExclamationCircleIcon className="h-6 w-6 text-[#E76F2B]" />
        </Box>
      )}
      <Box className="ml-3 mr-1 flex flex-1 flex-col">
        <Typography className="text-grey-70 font-bold" variant="caption">
          Business insurance • {transformDateStrToTimeString(created_at, { format: 'DD MMM. HH:mm' })}
        </Typography>
        <Typography className="text-black-secondary font-medium" variant="subtitle1">
          {detail}
        </Typography>
        <Box className="flex flex-row">
          {canRenew && (
            <Button
              className="text-brand-primary mr-2 px-0 font-bold"
              variant="text"
              endIcon={<ChevronRightIcon className="text-brand-primary h-4 w-4" />}
              onClick={jumpRenewPolicy}
            >
              Update policy renew
            </Button>
          )}
          {canExtend && (
            <Button
              className="text-brand-primary mr-2 px-0 font-bold"
              variant="text"
              endIcon={<ChevronRightIcon className="text-brand-primary h-4 w-4" />}
              onClick={jumpExtendPolicy}
            >
              Extend policy
            </Button>
          )}
          {canJumpUrl && (
            <Button
              className="text-brand-primary mr-2 px-0 font-bold"
              variant="text"
              endIcon={<ChevronRightIcon className="text-brand-primary h-4 w-4" />}
              onClick={jumpUrl}
            >
              Update payment method
            </Button>
          )}
        </Box>
      </Box>
      {!read && <Box className="bg-brand-secondary h-3 w-3 rounded-full"></Box>}
    </Box>
  );
};

export default NotificationsCard;
